import React from 'react';
import { Spin } from 'antd';
import { useNotifications } from '../hooks/useNotifications';
import { useUnreadCount } from '../hooks/useUnreadCount';
import { useMarkAsRead } from '../hooks/useMarkAsRead';
// import { useEnableNotifications } from '../hooks/useEnableNotifications';
// import { useDisableNotifications } from '../hooks/useDisableNotifications';
import { Bell, Check } from 'lucide-react';
import { notifyUser } from '../util/util';

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const NotificationsDrawer = ({ onClose }) => {
  const { data: notificationsData, isLoading, error, refetch } = useNotifications();
  const { refetch: refetchUnreadCount } = useUnreadCount();
  const markAsReadMutation = useMarkAsRead();
  // const disableMutation = useDisableNotifications();

  // const [notificationsEnabled, setNotificationsEnabled] = useState(true); // Assume enabled by default

  const [localNotifications, setLocalNotifications] = React.useState([]);
  React.useEffect(() => {
    if (notificationsData?.data) setLocalNotifications(notificationsData.data);
  }, [notificationsData]);
  const notifications = localNotifications || [];
  // const unreadCount = unreadCountData?.data || 0;

  // Optimistic toggle: update UI immediately, call API, revert on error
  const handleToggleRead = (id) => {
    const current = localNotifications.find(n => n.id === id);
    if (!current) return;

    const prev = localNotifications;
    const isCurrentlyRead = !!current.read_at;
    // Toggling the state: if read_at exists, set to null (unread); otherwise, set new date (read)
    const updated = prev.map(n => n.id === id ? { ...n, read_at: isCurrentlyRead ? null : new Date().toISOString() } : n);
    setLocalNotifications(updated);

    markAsReadMutation.mutate(id, {
      onSuccess: () => {
        // Server updated; refetch to get canonical data and update unread count
        refetch();
        refetchUnreadCount();
        notifyUser('success', isCurrentlyRead ? 'Notification marked as unread' : 'Notification marked as read');
      },
      onError: (error) => {
        // revert UI
        setLocalNotifications(prev);
        notifyUser('error', error?.response?.data?.message || 'Failed to update notification status');
      }
    });
  };
  
  // Renamed from handleMarkIfUnread for clarity. This handles the general click to mark as read.
  const handleMarkAsReadIfUnread = (id) => {
    const current = localNotifications.find(n => n.id === id);
    if (!current || current.read_at) return;
    
    // Optimistic update for marking as read
    const prev = localNotifications;
    const updated = prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n);
    setLocalNotifications(updated);

    markAsReadMutation.mutate(id, {
      onSuccess: () => {
        refetch();
        refetchUnreadCount();
        notifyUser('success', 'Notification marked as read');
      },
      onError: (error) => {
        setLocalNotifications(prev); // Revert UI
        notifyUser('error', error?.response?.data?.message || 'Failed to mark as read');
      }
    });
  };

    // We'll show the unread count in the header to provide quick context to users


  // const handleToggleNotifications = () => {
  //    if (notificationsEnabled) {
  //      disableMutation.mutate('', {
  //        onSuccess: () => {
  //          setNotificationsEnabled(false);
  //          notifyUser('success', 'Notifications disabled');
  //          refetch();
  //          refetchUnreadCount();
  //        },
  //        onError: (error) => notifyUser('error', error?.response?.data?.message || 'Failed to disable notifications'),
  //      });
  //    } else {
  //      enableMutation.mutate('', {
  //        onSuccess: () => {
  //          setNotificationsEnabled(true);
  //          notifyUser('success', 'Notifications enabled');
  //          refetch();
  //          refetchUnreadCount();
  //        },
  //        onError: (error) => notifyUser('error', error?.response?.data?.message || 'Failed to enable notifications'),
  //      });
  //    }
  // };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with controls */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }} className="mobile-hidden">
  Notifications
</h3>

        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Settings button placeholder */}
        </div>
      </div>

      {/* Notifications list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}><Spin /></div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#f00' }}>
            Error loading notifications
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            <Bell size={32} style={{ marginBottom: 16, opacity: 0.5 }} />
            <p>No notifications yet</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                background: notification.read_at ? '#fff' : '#f9f9f9',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                // Prevent click if clicking on button or any child of the button
                if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
                handleMarkAsReadIfUnread(notification.id);
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#000', lineHeight: 1.3 }}>
                    {notification.data.title || 'Notification'}
                  </h4>
                  {!notification.read_at && (
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#00b050',
                      flexShrink: 0,
                      marginTop: 2
                    }} />
                  )}
                </div>
                <p style={{ margin: '4px 0', color: '#666', fontSize: 13, lineHeight: 1.4 }}>
                  {notification.data.message}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ margin: 0, color: '#999', fontSize: 11 }}>
                    {formatDate(notification.created_at)}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // This button uses the general toggle, simplifying the UI logic
                      handleToggleRead(notification.id);
                    }}
                    className={`btn ${notification.read_at ? 'btn-white' : 'btn-primary'} mb-0 w-50`}
                    aria-pressed={!!notification.read_at}
                    aria-label={notification.read_at ? 'Unread' : 'Read'}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
                  >
                    <Check size={12} />
                    {notification.read_at ? 'Unread' : 'Read'}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

};

export default NotificationsDrawer;